<template>
  <q-field
    ref="field"
    v-bind="$attrs"
    :model-value="!!address"
    :disable="disable"
    :readonly="readonly"
    class="address-field"
    :class="{'q-field--clickable': isEditable, 'required-astrx-container': required, 'address-single-line': singleLine}"
    :error="!!errorMessage"
    :error-message="errorMessage || undefined"
    @focus="onFieldFocus"
  >
    <template #prepend>
      <q-avatar
        rounded
        icon="mdi-map-marker"
        font-size="0.75em"
        :color="highlightedIcon ? 'blue-7' : 'grey-3'"
        :text-color="highlightedIcon ? 'white' : 'black'"
      />
    </template>

    <template #control>
      <div class="col-12">
        <q-item-label v-if="!!addressStr" :lines="singleLine ? 2 : undefined">
          <nl2br v-if="!singleLine" :text="addressStr" />
          <span v-else>
            {{ addressStr }}
          </span>
        </q-item-label>

        <q-item-label v-else class="opaque-70">
          {{ addressPlaceholder }}
        </q-item-label>

        <slot />
      </div>
    </template>

    <template v-if="isEditable && !!address" #after>
      <cancel-button @click="onClear" />
    </template>
  </q-field>

  <AddressDialog
    v-if="isEditable && !!address && !disable && !readonly"
    ref="dialog"
    required
    :address="address"
    :autocomplete-options="autocompleteOptions"
    :search-loading="searchLoading"
    :suggestions-label="suggestionsLabel"
    :allow-refresh-suggestions="allowRefreshSuggestions"
    :suggestions="suggestions"
    :error-message="errorMessage"
    :title="dialogTitle"
    :auto-remove="autoClear"
    @remove="onClear"
    @load-suggestions="$emit('loadSuggestions')"
    @refresh-suggestions="$emit('refreshSuggestions')"
  >
    <template v-if="!!$slots['dialog-before']" #before>
      <slot name="dialog-before" />
    </template>

    <template v-if="!!$slots['dialog-after']" #after>
      <slot name="dialog-after" />
    </template>
  </AddressDialog>
</template>

<script>
import AddressDialog from 'components/partials/address/AddressDialog.vue'

import searchMixin from 'components/partials/address/searchMixin'
import { getFullAddressStr } from 'components/partials/PhoneAddressEmail'

/**
 * AddressField wraps all other address components into a single clickable field.
 * It implements Dialog, InputContainer and SearchLine
 *
 * SLOTS:
 * - default: content to display within the QField content area
 * - before: content to prepend before the address fields within the dialog
 * - after: content to append after the address fields within the dialog
 *
 * EMITS:
 * - create: When address is null, event will be emit automatically when QField is clicked to prompt creation of address object
 * - clear: emit when dialog is closed and autoClear=true, or when user clicks "Remove" within dialog
 */
export default {
  components: {
    AddressDialog
  },
  mixins: [searchMixin],
  props: {
    address: {
      type: Object,
      default: null
    },
    // Display * for address line 1
    required: {
      type: Boolean,
      default: false
    },
    // Disable clicking the QField
    disable: {
      type: Boolean,
      default: false
    },
    // Make QField readonly
    readonly: {
      type: Boolean,
      default: false
    },
    // Display marker in a blue color
    highlightedIcon: {
      type: Boolean,
      default: false
    },
    // Display the formatted address as a single line
    singleLine: {
      type: Boolean,
      default: false
    },
    // Placeholder when address is unspecified/null
    addressPlaceholder: {
      type: String,
      default: 'Enter Address...'
    },
    // Display error message under QField and within wrapper Dialog
    errorMessage: {
      type: String,
      default: null
    },
    // Dialog title
    dialogTitle: {
      type: String
    },
    // When wrapper dialog is closed, emit "remove" if the address object kv pairs are null
    autoClear: {
      type: Boolean,
      default: false
    }
  },
  emits: ['clear', 'create'],
  computed: {
    isEditable () {
      return !this.disable && !this.readonly
    },
    addressStr () {
      if (!this.address) {
        return null
      }
      return getFullAddressStr(this.address, !this.singleLine) || null
    }
  },
  methods: {
    onClear () {
      this.$emit('clear')
    },
    onFieldFocus () {
      // Remove focus, otherwise it will keep re-calling @focus after dialog is closed
      this.$refs.field.blur()

      if (!this.address) {
        this.$emit('create')
      }

      this.$nextTick(() => {
        if (this.$refs.dialog) {
          this.$refs.dialog.open()
        }
      })
    },
    focus () {
      if (this.$refs.field) {
        this.$refs.field.focus()
      }
    }
  }
}
</script>
